import { useMutation, useSuspenseQuery, type UseMutationOptions } from '@tanstack/react-query';
import { deleteProcedure, getProcedures, postNewProcedure } from './api';
import { CreateProcedure, PostNewProcedure } from './entity';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { queryClient } from '@shared/queries';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

export const useSuspenseProceduresQuery = (visitId: string) => {
  return useSuspenseQuery<CreateProcedure[]>({
    queryKey: ['procedures', visitId],
    queryFn: () => getProcedures(visitId),
  });
};

export const useProceduresMutation = (
  options?: UseMutationOptions<CreateProcedure, unknown, PostNewProcedure>,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('procedure.mutation');

  const mutation = useMutation({
    mutationFn: (data: PostNewProcedure) => postNewProcedure(data.visitId, data),

    onSuccess: (data: CreateProcedure, variables, context, meta) => {
      options?.onSuccess?.(data, variables, context, meta);
      queryClient.invalidateQueries({ queryKey: ['procedures', data.visitId] });
      queryClient.invalidateQueries({ queryKey: ['stockItems'] });
      addSnackBarMessage({ text: t('updateSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: t('updateError'), type: 'error' });
    },
  });

  return { mutation };
};

export const useDeleteProcedureMutation = (
  options?: UseMutationOptions<string, unknown, string>,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('procedure.mutation');

  const mutation = useMutation({
    mutationFn: (procedureId: string) => deleteProcedure(procedureId),

    onSuccess: (procedureId: string, variables, context, meta) => {
      options?.onSuccess?.(procedureId, variables, context, meta);
      queryClient.invalidateQueries({ queryKey: ['procedures', procedureId] });
      queryClient.invalidateQueries({ queryKey: ['stockItems'] });
      addSnackBarMessage({ text: t('deleteSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: t('deleteError'), type: 'error' });
    },
  });

  return { mutation };
};
